import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingFormField,
  DocumentCraftingLanguage,
  DocumentCraftingRevisionSummary,
  DocumentCraftingRunBrief,
  DocumentCraftingRunStatus,
  DocumentCraftingTypeKey,
  isRunTerminal,
} from "../../../Models/documentCrafting";
import { localizedText } from "../Common/useDocumentCraftingEnumLabels";
import { PreviewUnit } from "../Common/PagePreview";

// Pure helpers of the detail page (no React).

/** `?revision=draft` shows the caller's open draft. */
export const DRAFT_PARAM = "draft";

export type RevisionSelection =
  | { kind: "draft"; revision: DocumentCraftingRevisionSummary }
  | {
      kind: "saved";
      revision: DocumentCraftingRevisionSummary;
      isLatest: boolean;
    }
  | { kind: "none" };

/**
 * The revision the page shows for `?revision=<number|draft>`: the saved
 * revision with that number, the caller's draft, or the latest saved one.
 * `missing` is the requested number when it does not exist.
 */
export const selectRevision = (
  document: Pick<DocumentCraftingDocumentDetail, "revisions" | "my_draft_revision">,
  param: string | null,
): { selection: RevisionSelection; missing: string | null } => {
  const latest = document.revisions[0];
  const draft = document.my_draft_revision;
  const fallback: RevisionSelection = latest
    ? { kind: "saved", revision: latest, isLatest: true }
    : { kind: "none" };

  if (!param) return { selection: fallback, missing: null };
  if (param === DRAFT_PARAM) {
    return draft
      ? { selection: { kind: "draft", revision: draft }, missing: null }
      : { selection: fallback, missing: null };
  }
  const number = Number(param);
  const match = Number.isInteger(number)
    ? document.revisions.find((revision) => revision.number === number)
    : undefined;
  if (match) {
    return {
      selection: {
        kind: "saved",
        revision: match,
        isLatest: match.id === latest?.id,
      },
      missing: null,
    };
  }
  return { selection: fallback, missing: param };
};

/** The other primary language, for the translation preview. */
export const otherLanguage = (
  language: DocumentCraftingLanguage,
): DocumentCraftingLanguage => (language === "de" ? "en" : "de");

export const previewUnit = (typeKey: DocumentCraftingTypeKey): PreviewUnit =>
  typeKey === "presentation" ? "slide" : "page";

/** File extension of the native format when the type is not loaded. */
export const fallbackNativeExt = (typeKey: DocumentCraftingTypeKey) =>
  typeKey === "presentation" ? "pptx" : "docx";

/** A run that is still working (not finished, not waiting for answers). */
export const isRunWorking = (run: DocumentCraftingRunBrief | null | undefined) =>
  !!run &&
  !isRunTerminal(run.status) &&
  run.status !== DocumentCraftingRunStatus.AWAITING_ANSWERS;

const findField = (
  fields: DocumentCraftingFormField[] | null | undefined,
  key: string,
) => (fields ?? []).find((field) => field.key === key);

/**
 * The label of a form field path from a quality note, e.g. "contact.phone"
 * or "packages[1].price" → "Contact › Phone". Unknown paths come back as is.
 */
export const formFieldLabel = (
  fields: DocumentCraftingFormField[] | undefined,
  path: string,
  language: string,
): string => {
  if (!fields?.length) return path;
  const direct = findField(fields, path);
  if (direct) return localizedText(direct.label, language) || path;

  const segments = path
    .split(/[.[\]]+/)
    .filter((segment) => segment && !/^\d+$/.test(segment));
  const names: string[] = [];
  let level: DocumentCraftingFormField[] | null | undefined = fields;
  for (let index = 0; index < segments.length; index += 1) {
    const field = findField(level, segments[index]);
    if (!field) return names.length ? `${names.join(" › ")} › ${segments.slice(index).join(".")}` : path;
    names.push(localizedText(field.label, language) || field.key);
    level = field.fields?.length ? field.fields : field.item_fields;
  }
  return names.join(" › ") || path;
};
